import React from 'react';
import InspectDrawerShell from './InspectDrawerShell';
import ExposedPortsGroups from './ExposedPortsGroups';

function dnsRecordLines(dns) {
  if (!dns || typeof dns !== 'object') return [];
  const lines = [];
  const push = (label, values) => {
    if (!values) return;
    if (Array.isArray(values) && values.length === 0) return;
    if (typeof values === 'object' && !Array.isArray(values)) {
      lines.push(`${label}: ${JSON.stringify(values)}`);
      return;
    }
    lines.push(`${label}: ${values.join(', ')}`);
  };
  push('A', dns.A);
  push('AAAA', dns.AAAA);
  push('MX', (dns.MX || []).map((mx) => `${mx.preference} ${mx.host}`));
  push('NS', dns.NS);
  push('CNAME', dns.CNAME);
  push('TXT', (dns.TXT || []).map((t) => (t.length > 80 ? `${t.slice(0, 77)}...` : t)));
  if (dns.SOA) push('SOA', [dns.SOA.mname, dns.SOA.rname].filter(Boolean));
  return lines;
}

/**
 * Native email infrastructure inspector — right slide-over drawer.
 *
 * @param {{ isOpen: boolean, onClose: Function, data?: object }} props
 */
export default function EmailInfraInspectModal({ isOpen, onClose, data }) {
  if (!isOpen || !data) return null;

  const details = data.details && typeof data.details === 'object' ? data.details : {};
  const modal = details.infra_inspect && typeof details.infra_inspect === 'object'
    ? details.infra_inspect
    : {};
  const roleRaw = String(details.email_infra_role || 'domain');
  const roleLabel = roleRaw === 'mx_host' ? 'MX Host Infrastructure' : 'Domain Infrastructure';
  const hostname = modal.hostname || details.hostname || '';
  const ports = modal.exposed_ports || details.exposed_ports || [];
  const webServer = modal.web_server || details.web_server || 'Not observed';
  const sslIssuer = modal.ssl_issuer || details.ssl_issuer || 'Not observed';
  const asOrg = modal.as_org || details.as_org || 'Unknown';
  const hostCount = modal.host_count ?? details.host_count ?? '—';
  const totalServices = modal.total_services ?? details.total_services ?? '—';
  const emailDomain = details.email_domain || '';
  const dnsLines = dnsRecordLines(modal.dns || details.dns_records);
  const asnRows = Array.isArray(modal.asn) ? modal.asn : (Array.isArray(details.asn) ? details.asn : []);
  const tls = modal.tls && typeof modal.tls === 'object' ? modal.tls : (details.tls || {});
  const sourceHref = String(data.url || data.inspect_url || '').trim()
    || (hostname ? `https://${hostname}` : '');

  return (
    <InspectDrawerShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="Email infrastructure inspection"
      testId="email-infra-inspect-modal"
      eyebrow="Email Infrastructure"
      title={roleLabel}
      sourceHref={sourceHref}
      subtitle={
        <>
          {hostname ? (
            <p className="mt-1 font-mono text-[11px] text-slate-400">{hostname}</p>
          ) : null}
          {emailDomain ? (
            <p className="mt-0.5 font-mono text-[10px] text-slate-500">
              Mailbox domain: {emailDomain}
            </p>
          ) : null}
        </>
      }
    >
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 sm:col-span-2">
          <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Exposed Ports
          </dt>
          <dd className="mt-2">
            <ExposedPortsGroups ports={ports} />
          </dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
          <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Web Server
          </dt>
          <dd className="mt-1 text-slate-200">{webServer}</dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
          <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            SSL Issuer
          </dt>
          <dd className="mt-1 text-slate-200">{sslIssuer}</dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3 sm:col-span-2">
          <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            AS Organization (Team Cymru)
          </dt>
          <dd className="mt-1 text-slate-200">{asOrg}</dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
          <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Hosts
          </dt>
          <dd className="mt-1 font-bold text-slate-200">{hostCount}</dd>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-3">
          <dt className="text-[9px] font-black uppercase tracking-widest text-slate-500">
            Services
          </dt>
          <dd className="mt-1 font-bold text-slate-200">{totalServices}</dd>
        </div>
      </dl>

      {tls?.subject_cn || tls?.not_after ? (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
          <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
            TLS Certificate
          </p>
          {tls.subject_cn ? (
            <p className="font-mono text-[11px] text-slate-300">CN: {tls.subject_cn}</p>
          ) : null}
          {tls.not_after ? (
            <p className="mt-1 font-mono text-[11px] text-slate-400">Expires: {tls.not_after}</p>
          ) : null}
        </div>
      ) : null}

      {dnsLines.length > 0 ? (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
          <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
            DNS Records
          </p>
          <ul className="m-0 max-h-40 list-none space-y-1 overflow-y-auto p-0 font-mono text-[10px] text-slate-400">
            {dnsLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {asnRows.length > 0 ? (
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
          <p className="mb-2 text-[9px] font-black uppercase tracking-widest text-slate-500">
            ASN Details
          </p>
          <ul className="m-0 list-none space-y-1 p-0 font-mono text-[10px] text-slate-400">
            {asnRows.map((row) => (
              <li key={`${row.ip}-${row.asn}`}>
                {row.ip}: {row.asn || '—'} {row.country ? `(${row.country})` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </InspectDrawerShell>
  );
}
